import React, { useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Button, Typography, Card, useTheme } from '@mui/material'

import { J2Validate } from '@e3dc-react/icons'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AvailableTest } from 'src/Libs/installation-details/dto/hardware-test-result.dto'
import { HardwareTestResultModel } from 'src/Libs/installation-details/models'
import { getHardwareTestMessage } from '../../../../utils/get-hardware-test-message'
import ReactMarkdown from 'react-markdown'
import { getReactMarkdownComponents } from 'src/Libs/notifications/config'
import remarkGfm from 'remark-gfm'
import rehype from 'rehype-raw'

const useStyles = makeStyles()(theme => ({
  contentContainer: {
    width: '100%',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '260px',
    marginTop: theme.spacing(2),
    '@media (max-width: 650px)': {
      paddingInline: theme.spacing(2),
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper2,
    border: `1px solid ${theme.palette.background.paperBorder}`,
    borderRadius: theme.shape.borderRadius,
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    width: '100%',
    maxWidth: '600px',
    '@media (max-width: 650px)': {
      textAlign: 'center',
    },
  },
  selectContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'baseline',
    width: '100%',
  },
  select: {
    marginTop: theme.spacing(2),
  },
  button: {
    marginBlock: theme.spacing(4),
  },
  cardContainer: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: '600px',
  },
  testDescriptionCard: {
    boxShadow: 'none',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    width: '100%',
  },
}))

interface Props {
  data: HardwareTestResultModel
  selectedTest: AvailableTest | undefined
  onChange: (value: AvailableTest) => void
  onSelectionApply: () => void
}

/**
 * Component to display available hardware tests to perform
 */
const SelectTest: React.FC<Props> = ({ data, selectedTest, onChange, onSelectionApply }) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const reactMarkdownComponents = useMemo(() => getReactMarkdownComponents(theme), [theme])

  const options = data.availableTests.map(test => ({
    label: getHardwareTestMessage(test.name, t),
    value: test,
  }))

  const descriptionCardData = data.availableTests.find(elem => elem.testId === selectedTest?.testId)

  return (
    <Box className={classes.contentContainer}>
      <Box display="flex">
        <J2Validate color="primary" />
        <Typography variant="body1">{t('system-details.hardware-test.test-performed')}</Typography>
      </Box>
      <Box className={classes.cardContainer}>
        <ReactMarkdown components={reactMarkdownComponents} remarkPlugins={[remarkGfm]} rehypePlugins={[rehype]} className={cx(classes.card)}>
          {getHardwareTestMessage(data.message, t)}
        </ReactMarkdown>
        <Box className={classes.card}>
          <Box className={classes.selectContainer}>
            <Typography>{t('system-details.hardware-test.select-test')}</Typography>
            <Select
              label={t('system-details.hardware-test.test')}
              value={selectedTest}
              onChange={onChange}
              options={options}
              className={classes.select}
              menuShouldBlockScroll={false}
            />
            {descriptionCardData && (
              <Card variant="outlined" className={classes.testDescriptionCard}>
                <Typography variant="body2">{getHardwareTestMessage(descriptionCardData.name, t)}</Typography>
                <Typography variant="caption">{getHardwareTestMessage(descriptionCardData.description, t)}</Typography>
              </Card>
            )}
          </Box>
        </Box>
      </Box>
      <Button className={classes.button} variant="contained" onClick={onSelectionApply} disabled={!selectedTest}>
        {t('system-details.hardware-test.select-test-button')}
      </Button>
    </Box>
  )
}

export default SelectTest
